import {ReactElement} from 'react';
import store from '../../store/store';
import IconButton from "../../ui/IconButton/IconButton";
import Icons from "../../ui/IconButton/Icons";
import util from '../../lib/util';
import ChartProps from '../../Charts/ChartProps';
import mathUtils from '../../lib/mathUtils';
import MilestoneHelper, {getMilestones} from './Milestones/MilestoneHelper';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {getBenefactorIds} from '../../Case/caseParties';
import {formatFMLFromId, formatUniqueName, replaceNames} from "../../Contacts/ContactFormat";
import find from "../../store/find";
import renderClientLabels from "../../lib/picos/RenderClientLabel";
import Lead from "../../entities/lead";
import type {uuid} from "../../@types/schema";
import ArrayMap from "../../lib/ArrayMap";
import Milestone from "../../entities/milestone";
import Task from "../../entities/task";
import Contact from '../../entities/contact';
import Participant from "../../entities/participant";
import { SelectOption } from '../../@types/select';
import {getLeadStanding, getResolution, type LeadStanding, type ResolutionType} from './Milestones/milestonePolicy';

export type PipelineLeadState = {
    lead: Lead;
    milestones: Milestone[];
    position: number;
    outcome: ResolutionType | null;
    terminal: boolean;
    enteredOn: Date | null;
};

export type LeadPathData = {
    nodes: Array<{ id: string }>;
    links: Array<{ source: string; target: string; value: number }>;
    leads: PipelineLeadState[];
}

/**
 * Helper class for matters and invoices.
 * @class
 */
class LeadHelper {
    static leadFlowColors = [
        '#146CF6', '#188AF0', '#00B7D8', '#00D4B0', '#00E54B', 'green', 'green', 'pink', 'red', 'yellow'
    ];

    // STAGES is a memoized lazy getter so its construction does NOT fire
    // during LeadHelper's module evaluation. The previous eager
    // `static STAGES = [...]` initializer read MilestoneHelper.S_*/T_*,
    // which are themselves lazy i18n getters — eager construction here
    // therefore reintroduced the very TDZ that MilestoneHelper's lazy
    // conversion was meant to defuse. Defer the entire array to first
    // call-site access (post-module-init), and cache it after that.
    // `tooltip` is `string | ReactElement` because `MilestoneHelper.T_*` reads
    // `i18n.d(...)`, which returns a ReactElement for translations that need
    // interpolated nodes and a plain string otherwise. `name` stays `string`
    // because `MilestoneHelper.S_*` uses `i18n.ls(...)` (string-only).
    private static _STAGES: Array<{position: number; name: string; tooltip: string | ReactElement; color: string}> | null = null;
    static get STAGES() {
        if (!LeadHelper._STAGES) {
            LeadHelper._STAGES = [
                {position: MilestoneHelper.N_AWAITING_INFO,        name: MilestoneHelper.S_AWAITING_INFO,        tooltip: MilestoneHelper.T_AWAITING_INFO,        color: LeadHelper.leadFlowColors[0]},
                {position: MilestoneHelper.N_CONTACT_INFO_RECEIVED, name: MilestoneHelper.S_CONTACT_INFO_RECEIVED, tooltip: MilestoneHelper.T_CONTACT_INFO_RECEIVED, color: LeadHelper.leadFlowColors[1]},
                {position: MilestoneHelper.N_CONTACT_MADE,         name: MilestoneHelper.S_CONTACT_MADE,         tooltip: MilestoneHelper.T_CONTACT_MADE,         color: LeadHelper.leadFlowColors[2]},
                {position: MilestoneHelper.N_APPOINTMENT_MADE,     name: MilestoneHelper.S_APPOINTMENT_MADE,     tooltip: MilestoneHelper.T_APPOINTMENT_MADE,     color: LeadHelper.leadFlowColors[3]},
                {position: MilestoneHelper.N_INITIAL_CONSULT,      name: MilestoneHelper.S_INITIAL_CONSULT,      tooltip: MilestoneHelper.T_INITIAL_CONSULT,      color: LeadHelper.leadFlowColors[4]},
                {position: MilestoneHelper.N_RETAINER_SIGNED,      name: MilestoneHelper.S_RETAINER_SIGNED,      tooltip: MilestoneHelper.T_RETAINER_SIGNED,      color: LeadHelper.leadFlowColors[5]},
                {position: MilestoneHelper.N_RETAINER_FUNDED,      name: MilestoneHelper.S_RETAINER_FUNDED,      tooltip: MilestoneHelper.T_RETAINER_FUNDED,      color: LeadHelper.leadFlowColors[6]},
                {position: MilestoneHelper.N_CONSULT_SUFFICIENT,   name: MilestoneHelper.S_CONSULT_SUFFICIENT,   tooltip: MilestoneHelper.T_CONSULT_SUFFICIENT,   color: LeadHelper.leadFlowColors[7]},
                {position: MilestoneHelper.N_REFUSED,              name: MilestoneHelper.S_REFUSED,              tooltip: MilestoneHelper.T_REFUSED,              color: LeadHelper.leadFlowColors[8]},
                {position: MilestoneHelper.N_LOST,                 name: MilestoneHelper.S_LOST,                 tooltip: MilestoneHelper.T_LOST,                 color: LeadHelper.leadFlowColors[9]},
                {position: MilestoneHelper.N_WAITING_ON,           name: MilestoneHelper.S_WAITING_ON,           tooltip: MilestoneHelper.T_WAITING_ON,           color: LeadHelper.leadFlowColors[10]},
            ];
        }
        return LeadHelper._STAGES;
    }

    static PARKING_LOT = <span><FontAwesomeIcon icon={Icons.PARKING.reactIcon}/> Parking Lot</span>;

    /**
     * Get a lead from its id.
     *
     * @param {uuid} id
     * @param {Lead} unsavedEntity
     * @returns {Lead}
     */
    static getLeadFromId = (id: uuid, unsavedEntity?: Lead): Lead => {
        if (unsavedEntity && unsavedEntity.id === id) return unsavedEntity;
        return store.getState().leads.get(id);
    }

    /**
     * Return a list of leads where the clients are part of the Lead.
     * @param {Array.<uuid>} clientIds
     * @param {boolean} [matchAll] - all clientIds must match!
     * @param {Lead} [unsavedEntity]
     */
    static getLeadsForClients = (clientIds: uuid[], matchAll: boolean = false, unsavedEntity?: Lead) => {
        // Find all leads where one of the clients is in the clientIds list
        const leads = store.getState().leads
                           .filter(lead => {
                               const _clientIds = lead.clients || [];
                               const inCommon = _clientIds.filter(value => clientIds.includes(value));
                               if (matchAll) return inCommon.length === clientIds.length;
                               return inCommon.length > 0;
                           });

        if (unsavedEntity && !leads.find(l => l.id === unsavedEntity.id)) leads.push(unsavedEntity);

        return leads;
    };

    /**
     *
     * @param {Lead} lead
     * @returns {string}
     */
    static getCaseTypesAsString = (lead: Lead): string => {
        return lead.caseTypes.map(caseTypeId => {
            // Reconstitute the caseType from ID to CaseType
            const caseType = store.getState().caseTypes.get(caseTypeId);

            return `${caseType.category} ${caseType.subcategory ? '(' + caseType.subcategory + ')' : ''}`.trim();
        }).join(', ');
    };

    /**
     * Where the lead stands: its resolution, outcome, whether it is open, and the milestone that names its stage.
     * Every outcome test below reads this (milestonePolicy.getLeadStanding, OWA-1678), so no two views can
     * disagree about a lead.
     *
     * @param {Lead} lead
     * @returns {LeadStanding}
     */
    static getStanding = (lead: Lead): LeadStanding => {
        return getLeadStanding(LeadHelper.getMilestones(lead));
    }

    /**
     * A qualified lead has passed Contact Made but has not been Refused.
     *
     * @param {Lead} lead
     */
    static isQualified = (lead: Lead) => {
        const milestones = LeadHelper.getMilestones(lead);
        const passedContactMade = milestones.some(ms => ms.status !== 'D' && ms.position === MilestoneHelper.N_CONTACT_MADE);

        return passedContactMade && getLeadStanding(milestones).outcome !== 'refused';
    }

    /**
     * Open means no terminal resolution: not won, lost, refused or consult-sufficient. A lead in the Parking Lot
     * is open, and an estimated close date never closes a lead.
     *
     * @param {Lead} lead
     */
    static isOpen = (lead: Lead): boolean => {
        return LeadHelper.getStanding(lead).open;
    }

    /**
     * Check if this lead is marked won.
     *
     * @param {Lead} lead
     * @param {boolean} strict - if strict, only return leads that are won (not consult sufficient)
     * @returns {boolean} - true if won
     */
    static isWon = (lead: Lead, strict?: boolean): boolean => {
        const outcome = LeadHelper.getStanding(lead).outcome;
        return outcome === 'won-retainer' || (!strict && outcome === 'won-consult');
    }

    /**
     * Return true if consult was sufficient.
     *
     * @param {Lead} lead
     * @returns {boolean}
     */
    static isConsultSufficient = (lead: Lead): boolean => {
        return LeadHelper.getStanding(lead).outcome === 'won-consult';
    }

    /**
     * Check if this lead is marked lost.
     *
     * @param {Lead} lead
     * @returns {boolean} - true if lost
     */
    static isLost = (lead: Lead): boolean => {
        return LeadHelper.getStanding(lead).outcome === 'lost';
    }

    /**
     * Check if this lead is marked refused.
     *
     * @param {Lead} lead
     * @returns {boolean} - true if refused
     */
    static isRefused = (lead: Lead): boolean => {
        return LeadHelper.getStanding(lead).outcome === 'refused';
    }

    /**
     * Render the lead as input for a Select component (label, value, data).
     *
     * @param {Lead} lead
     * @returns {SelectOption<string, Lead>}
     */
    static renderLead = (lead: Lead): SelectOption<string, Lead> => {
        const value = `${lead.name} ${lead.clients.map(cid => {
            const c = find<Contact>("contacts", cid);
            return c ? formatUniqueName(c) : 'Unknown'
        })}`;

        const name = window.__demo_mode__
                     ? replaceNames(lead.clients, value)
                     : lead.name;

        return {
            label: (
                <div key={lead.id} className='u-items middlewrap'>
                    <div key='icon' style={{margin: 'auto 5px auto 0'}}>
                        <IconButton type={Icons.LEAD} color='green'/>
                    </div>
                    <div key='name' className='u-verticalCenter'>{name}</div>
                    {renderClientLabels(lead.clients, 'lead', lead.id)}
                </div>),
            value,
            data: lead
        };
    }

    /**
     * Render a list of leads.
     * @param {Lead[]} leads
     * @returns {SelectOption<string, Lead>[]}
     */
    static renderLeads = (leads: Lead[] | ArrayMap<Lead>): SelectOption<string, Lead>[] => {
        return leads
            .map(lead => LeadHelper.renderLead(lead))
            .sort((a, b) => a.value.localeCompare(b.value));
    }

    /**
     * Get benefactors for the given leads: Benefactor participants and payers, never anyone
     * adverse (caseParties.getBenefactorIds, OWA-1341).
     *
     * Subscribed callers pass the participants slice so memoized derivations recompute on
     * sync; others fall back to the live store.
     *
     * @param {Lead | Lead[]} lead
     * @returns {uuid[]}
     */
    static getBenefactors = (lead: Lead | (Lead | null | undefined)[] | null | undefined, participants?: ArrayMap<Participant> | Participant[]): uuid[] => {
        if (!lead) {
            console.error('getBenefactors called with no lead');
            return [];
        }

        return getBenefactorIds(Array.isArray(lead) ? lead : [lead], participants);
    };

    // static getBapiForLead = memd((lead: Lead) => {
    //     // Keep track of the total influenced dollars, both billed and collected
    //     let billed = 0;
    //     let collected = 0;
    //
    //     // const matters = store.getState().matters.filter(matter => matter.leadId === lead.id);
    //     const matters = store.getState().matters.getMappingFor('leadId', lead.id);
    //
    //     // For each matter, get the influenced amount based on the campaign's lead influence.
    //     matters.forEach(matter => {
    //         // For this specific matter, calculate the amounts billed and collected.
    //         const bapis = FinancialHelper.getBilledAndPaidForMatters([matter.id]);
    //
    //         bapis.forEach((bapi, i) => {
    //             billed += bapi.billed;
    //             collected += bapi.collected;
    //         });
    //     });
    //
    //     return {
    //         lead,
    //         billed: util.toTwoDecimals(billed),
    //         collected: util.toTwoDecimals(collected)
    //     };
    // }, {
    //     args: 1,
    //     depFns: [() => store.getState().matters, () => store.getState().invoices],
    //    // maxAge: 60000
    // });

    /**
     * Return true if this lead went from milestone A to B.
     *
     * @param {ArrayMap} milestones
     * @param {Lead} lead
     * @param {number} a - milestone.position
     * @param {number} b - milestone.position
     * @returns {boolean}
     */
    static filterMilestoneAtoB = (milestones: ArrayMap<Milestone>, lead: Lead, a: number, b: number): boolean => {
        const mss = milestones.getMappingFor('entityId', lead?.id).filter(ms => !!ms.dateCompleted);

        // Find an entry in mss where we passed milestone a
        const foundA = mss.find(ms => ms.position === a);
        const foundB = mss.find(ms => ms.position === b);
        const inBetween = mss.find(ms => (ms.position > a) && (ms.position < b));

        return foundA && foundB && !inBetween;
    };

    /**
     * Get current milestones for a lead
     * @param lead
     * @returns {Milestone[]}
     */
    static getMilestones = (lead: Lead): Milestone[] => {
        if (!lead) {
            console.warn('No lead sent to getMilestones');
            return [];
        }

        // only consider milestones that have been completed
        return store.getState().milestones.getMappingFor('entityId', lead.id).filter(ms => !!ms.dateCompleted);
    };

    /**
     * Get all valid reasons for a lead to be won/lost/etc.
     *
     * @param {Lead} lead
     * @returns {string[]}
     */
    static getReasons = (lead: Lead): string[] => {
        return store.getState()
                    .milestones
                    .getMappingFor('entityId', lead?.id)
                    .filter(ms => !!ms.dateCompleted && ms.reasons?.length > 0)
                    .map(mss => {
                        return mss.reasons;
                    })
                    .flat()
                    .filter(util.existsFilter)
                    .map(rid => store.getState().lookups.get(rid)?.key)
                    .filter(util.existsFilter);
    }

    /** One current stage and named outcome per non-deleted lead. */
    static getPipelineLeadStates = (leads: ArrayMap<Lead> | Lead[], externalMilestones?: Milestone[], asOf?: Date): PipelineLeadState[] => {
        const unique = new Map<string, Lead>();
        leads.forEach(lead => {
            if (lead.status !== 'D' && (!asOf || (lead.createdOn && lead.createdOn <= asOf))) unique.set(lead.id, lead);
        });
        const byLead = new Map<string, Milestone[]>();
        (externalMilestones ?? store.getState().milestones).forEach(ms => {
            if (ms.status === 'D' || !ms.dateCompleted || (asOf && ms.dateCompleted > asOf)) return;
            if (!this.STAGES.some(stage => stage.position === ms.position)) return;
            const existing = byLead.get(ms.entityId) ?? [];
            existing.push(ms);
            byLead.set(ms.entityId, existing);
        });
        return Array.from(unique.values()).map(lead => {
            const milestones = byLead.get(lead.id) ?? [];
            const resolution = getResolution(milestones);
            const latest = [...milestones].sort((a, b) => b.position - a.position)[0];
            const current = resolution?.milestone ?? latest;
            return {lead, milestones, position: current?.position ?? MilestoneHelper.N_AWAITING_INFO,
                outcome: resolution?.def.type ?? null, terminal: resolution?.def.terminal ?? false,
                enteredOn: current?.dateCompleted ?? lead.createdOn ?? null};
        });
    };

    /** Money ($) or count (#) in each open stage, including the waiting parking lot. */
    static getSalesPipelineData = (leads: ArrayMap<Lead> | Lead[], type: '$' | '#', externalMilestones?: Milestone[]): Array<{ label: string; value: number; tooltip: string | ReactElement; color: string}> => {
        const stageMap = new Map<string, ArrayMap<Lead> | Lead[]>();

        this.STAGES.forEach(stage => {
            if (stage.position < MilestoneHelper.N_RETAINER_FUNDED ||
                stage.position === MilestoneHelper.N_WAITING_ON) {
                stageMap.set(stage.name, []);
            }
        });

        this.getPipelineLeadStates(leads, externalMilestones).forEach(state => {
            if (state.terminal) return;
            const stage = this.STAGES.find(stage => stage.position === state.position);
            if (stage) stageMap.get(stage.name)?.push(state.lead);
        });

        const result = Array.from(stageMap.keys()).map((label, i) => {
            const value = type === '$'
                          ? stageMap.get(label).reduce((t, c) => t + (c.estimatedValue ?? 0), 0)
                          : stageMap.get(label).reduce(t => t + 1, 0);

            const tooltip = this.STAGES.find(s => s.name === label)?.tooltip;
            return {label, value, tooltip, color: ChartProps.funnelColors[i]};
        });

        stageMap.clear();

        return result;
    }

    /**
     * Get the data for the Sankey chart (the Funnel).
     *
     * This will be a chart with flow going from milestone to milestone, and money (collected) in the flow.
     *
     * @param {Lead[]|ArrayMap<Lead>} leads
     * @param {'$'|'#'} type
     * @return {LeadPathData>}
     */
    static getLeadPathData = (leads: ArrayMap<Lead> | Lead[], type: '$' | '#', externalMilestones?: Milestone[]): LeadPathData => {
        const states = this.getPipelineLeadStates(leads, externalMilestones).filter(state => state.milestones.length > 0);
        const positions = MilestoneHelper.getStageNumbers();

        // Need a list of source => target, where target > source
        const sourceToTarget = positions.map(a => {
            const after = positions.filter(b => b > a).sort();

            return after.map(b => {
                // Find all leads where this happened
                const data = states.filter(state => {
                        const mss = state.milestones;
                        const foundA = mss.find(ms => ms.position === a);
                        const foundB = mss.find(ms => ms.position === b);
                        const inBetween = mss.find(ms => (ms.position > a) && (ms.position < b));
                        return foundA && foundB && !inBetween;
                    });
                const leadTotal = type === '#'
                                  ? data.length
                                  : mathUtils.sum(data.map(state => state.lead.estimatedValue ?? 0));

                return {
                    source: LeadHelper.STAGES.find(s => s.position === a)?.name,
                    target: LeadHelper.STAGES.find(s => s.position === b)?.name,
                    value: leadTotal
                };
            });
        }).flat().filter(m => m.value > 0);

        return {
            nodes: LeadHelper.STAGES.map(st => {
                return {id: st.name};
            }),
            links: sourceToTarget,
            leads: states,
        };
    };

    /**
     * Get all tasks for a lead.
     *
     * @param {Lead} lead
     * @returns {Task[]}
     */
    static getTasksForLead = (lead: Lead): Task[] => {
        return store.getState().tasks.getMappingFor('leadId', lead.id);
    };

    /**
     * Generate a lead name based on the client names and case types
     * @param {Lead} lead
     * @returns {string}
     */
    static generateName = (lead: Lead): string => {
        // The name will be ${caseTypes} for ${clientNames}
        const caseTypes = LeadHelper.getCaseTypesAsString(lead) || 'Unknown';
        const clientNames = lead.clients.map(cid => formatFMLFromId(cid)).join(' and ');
        return `${new Date().getFullYear()} ${caseTypes} for ${clientNames}`;
    }
}

export default LeadHelper;
